<!--
  Nuovo Spostamento Interno (journey) — gestionale admin.

  ⚠️ LA LOGICA NON È SCRITTA QUI. Vive in `composables/useJourneyLegs.js` e in
  `components/JourneyLegsEditor.vue`, portati dal gestionale partner senza
  modifiche funzionali: mission chaining, transito fra corse, cascata degli
  orari, guardia di inserimento e concorrenza driver sono regole già validate
  sul campo. Questa pagina si limita a scegliere il CONTESTO — partner e data —
  e a salvare.

  La differenza col gestionale partner è tutta lì: là il partner è l'utente
  loggato, qui va scelto. Da quella scelta dipendono due cose:
    - le SEDI che alimentano l'autocomplete di partenza e arrivo;
    - la CAPABILITY: un partner senza `journey_type` non può avere spostamenti
      interni, e il backend risponde 403. Meglio dirlo prima di far compilare
      un percorso intero.

  La disponibilità la verifica ogni corsa (capacità della provincia di partenza
  e driver liberi), nell'editor. `num_driver` del partner non limita più niente.
  Solo da qui Simba può salvare anche le corse non disponibili.
-->
<template>
  <div class="jny">
    <!-- ---------- intestazione ---------- -->
    <div class="jny__head">
      <button
        type="button"
        class="jny__back"
        @click="goBack"
      >
        ‹
      </button>
      <div>
        <h1 class="jny__title">
          Crea un nuovo percorso
        </h1>
        <p class="jny__sub">
          Spostamento interno · una o più corse nella stessa giornata
        </p>
      </div>
    </div>

    <!-- ---------- contesto: partner e data ---------- -->
    <section class="jny__bar">
      <div class="jny__field jny__field--partner">
        <label class="jny__lab">Partner</label>
        <el-select
          v-model="partnerId"
          filterable
          clearable
          placeholder="Scegli il partner"
          :loading="partnersLoading"
          style="width: 100%"
          @change="onPartnerChange"
        >
          <el-option
            v-for="p in partners"
            :key="p.id"
            :label="p.company_name"
            :value="p.id"
          />
        </el-select>
      </div>

      <div class="jny__field jny__field--date">
        <label class="jny__lab">Data</label>
        <!-- Data nel formato delle altre pagine: Element Plus non ha la lingua
             configurata e scriverebbe il nome del giorno in inglese. -->
        <el-date-picker
          v-model="journeyDate"
          type="date"
          format="DD/MM/YYYY"
          value-format="YYYY-MM-DD"
          placeholder="Seleziona una data"
          :disabled="!canPlan"
          style="width: 100%"
        />
      </div>
    </section>

    <!-- Il partner non è abilitato: dirlo subito evita di far compilare un
         percorso che il backend rifiuterebbe con un 403. -->
    <div
      v-if="partner && !canPlan"
      class="jny__warn"
    >
      <span class="jny__warnIco">⚠</span>
      <div>
        <b>{{ partner.company_name }} non è abilitato agli Spostamenti Interni.</b>
        Il percorso non può essere creato. Si abilita dalla scheda del partner,
        con il flag «Spostamenti interni».
      </div>
    </div>

    <!-- ---------- corse ---------- -->
    <template v-if="canPlan">
      <journey-legs-editor
        ref="legsEditor"
        :date="journeyDate || ''"
        :headquarters="headquarters"
        :partner-id="partnerId || ''"
      />

      <!-- ---------- azioni ---------- -->
      <div class="jny__actions">
        <!-- Forzatura di Simba: salva anche le corse senza disponibilità
             (provincia piena o nessun driver libero). Andranno coperte a mano. -->
        <el-checkbox
          v-model="ignoreAvailability"
          class="jny__force"
        >
          Salva anche le corse non disponibili
        </el-checkbox>
        <el-button @click="goBack">
          Annulla
        </el-button>
        <el-button
          type="primary"
          :loading="saving"
          @click="saveJourney"
        >
          Salva percorso
        </el-button>
      </div>
    </template>

    <p
      v-else-if="!partner"
      class="jny__empty"
    >
      Scegli prima il partner: le sedi dipendono da lui.
    </p>
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { ElMessage } from 'element-plus';
import partnerAPI from '../../services/partnerAPI.js';
import journeyAPI from '../../services/journeyAPI.js';
import JourneyLegsEditor from '../../components/JourneyLegsEditor.vue';

export default {
  name: 'AddJourneyPage',
  components: { JourneyLegsEditor },

  setup() {
    const router = useRouter();

    const partners = ref([]);
    const partnersLoading = ref(false);
    const partnerId = ref('');
    const partner = ref(null);
    const journeyDate = ref(null);
    // Forzatura di Simba: salvare anche le corse non disponibili.
    const ignoreAvailability = ref(false);
    const saving = ref(false);
    const legsEditor = ref(null);

    // Le sedi del partner alimentano l'autocomplete dell'editor.
    const headquarters = computed(() => partner.value?.headquarters || []);

    // Capability: senza `journey_type` il backend risponde 403.
    const canPlan = computed(() => !!partner.value?.journey_type);

    const loadPartners = async () => {
      partnersLoading.value = true;
      try {
        const r = await partnerAPI.getPartners();
        partners.value = r.data?.data ?? r.data ?? [];
      } catch (e) {
        console.warn('Elenco partner non disponibile:', e?.message);
      } finally {
        partnersLoading.value = false;
      }
    };
    loadPartners();

    const onPartnerChange = async (id) => {
      partner.value = null;
      journeyDate.value = null;
      if (!id) return;
      try {
        const r = await partnerAPI.getPartnerDetail(id);
        partner.value = r.data;
      } catch (e) {
        console.error('Partner non caricato:', e);
        ElMessage({ message: 'Partner non caricato', type: 'error' });
      }
    };

    const saveJourney = async () => {
      if (!partner.value) {
        ElMessage({ message: 'Scegli il partner', type: 'error' });
        return;
      }
      if (!journeyDate.value) {
        ElMessage({ message: 'Scegli una data', type: 'error' });
        return;
      }
      const editor = legsEditor.value;
      if (!editor) return;

      // Le regole di validità sono quelle dell'editor: punti, orari, dati auto
      // e disponibilità delle corse (che Simba può scegliere di superare).
      const check = editor.validate({ ignoreAvailability: ignoreAvailability.value });
      if (!check.ok) {
        ElMessage({ message: check.message, type: 'error' });
        return;
      }

      saving.value = true;
      try {
        const payload = editor.buildJourneyPayload(partner.value);
        if (ignoreAvailability.value) payload.ignore_availability = true;
        const resp = await journeyAPI.addJourney(payload);
        if (resp.status === 201 || resp.status === 200) {
          ElMessage({ message: 'Percorso creato', type: 'success' });
          // Si torna all'elenco Booking, non a Movimenti: gli spostamenti
          // interni compaiono già lì (una riga per corsa) e quella pagina
          // verrà riassorbita. Chi salva deve vedere il risultato.
          router.push('/booking');
        }
      } catch (e) {
        // 400 (disponibilità, validazioni del DTO) e 403 (capability):
        // il messaggio del server è più preciso di qualunque testo locale.
        const msg = e?.response?.data?.message;
        ElMessage({
          message: Array.isArray(msg) ? msg.join(' · ') : msg || 'Percorso non creato',
          type: 'error',
          duration: 6000,
        });
        console.error('saveJourney:', e?.response?.status, msg);
      } finally {
        saving.value = false;
      }
    };

    const goBack = () => router.back();

    return {
      partners, partnersLoading, partnerId, partner, headquarters,
      canPlan, journeyDate, ignoreAvailability, saving, legsEditor,
      onPartnerChange, saveJourney, goBack,
    };
  },
};
</script>

<style scoped>
.jny {
  padding: 22px clamp(16px, 3vw, 32px) 48px;
  max-width: 1180px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ----- intestazione ----- */
.jny__head {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.jny__back {
  width: 30px;
  height: 30px;
  flex: none;
  border: 1px solid var(--border-color);
  background: var(--bg-primary);
  border-radius: var(--radius-sm);
  font-size: 15px;
  color: var(--text-secondary);
  cursor: pointer;
  line-height: 1;
}
.jny__back:hover {
  color: var(--text-primary);
  border-color: var(--color-neutral-300);
}
.jny__title {
  margin: 0;
  font-size: 23px;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: var(--text-primary);
}
.jny__sub {
  margin: 3px 0 0;
  font-size: 13px;
  color: var(--text-secondary);
}

/* ----- barra di contesto ----- */
.jny__bar {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-sm);
  padding: 15px 18px;
}
.jny__field--partner { flex: 1 1 320px; min-width: 240px; }
.jny__field--date { flex: 0 1 260px; min-width: 200px; }
.jny__lab {
  display: block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--text-secondary);
  margin-bottom: 5px;
}

/* ----- avvisi ----- */
.jny__warn {
  display: flex;
  gap: 11px;
  align-items: flex-start;
  background: #fef9c3;
  border: 1px solid #fde047;
  border-radius: var(--radius-sm);
  padding: 12px 16px;
  font-size: 13px;
  line-height: 1.5;
  color: #713f12;
}
.jny__warnIco {
  flex: none;
  font-size: 15px;
  line-height: 1.3;
}

.jny__empty {
  margin: 0;
  padding: 28px;
  text-align: center;
  font-size: 13.5px;
  color: var(--text-tertiary);
  background: var(--bg-primary);
  border: 1px dashed var(--border-color);
  border-radius: var(--radius-sm);
}

/* ----- azioni ----- */
.jny__force {
  margin-right: auto;
}
.jny__actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-sm);
  padding: 14px 18px;
}
</style>
